import { useScheduleEnabled } from '@/hooks/useScheduleEnabled';
import { WorklistPage } from '@/pages/Worklist';
import { PATIENT_LIST_ORG_STORAGE_KEY } from '@/utils/organizationFilterStorage';
import {
  parseStoredWorklistDue,
  WORKLIST_ASSIGNED_TO_STORAGE_KEY,
  WORKLIST_DUE_STORAGE_KEY,
  type WorklistDueFilter,
} from '@/utils/worklistFilterStorage';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { lazy, Suspense } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { z } from 'zod';

// Lazy so the morning-briefing module graph (overlay/slideshow/player/appointments) isn't pulled into
// every home-page load — only fetched when the flag renders the button.
const MorningBriefingButton = lazy(async () => ({
  default: (await import('@/pages/CarePlanView/narration/MorningBriefingButton')).MorningBriefingButton,
}));

const WorklistSearchSchema = z.object({
  organizationId: z.string().min(1).optional(),
  /** Assignee filter: a practitioner id, or 'unassigned' for ownerless tasks (issue #8762). */
  assignedTo: z.string().min(1).optional(),
  /** Due-date filter: due today or earlier, no due date, or all tasks. The pre-rename 'overdue' is
   *  still accepted so old bookmarks and shared links keep working; it reads as 'due'. */
  due: z.enum(['due', 'none', 'all', 'overdue']).optional(),
  pageIndex: z.number().int().min(0).default(0),
  /** The task open in the sidebar, and the patient it belongs to. Both live in the URL so the open
   *  task survives a reload and can be shared; a task-line click already knows both. A
   *  `taskPatientId` on its own is the sidebar's create mode — a new task for that patient. */
  task: z.string().min(1).optional(),
  taskPatientId: z.string().min(1).optional(),
  showFlagged: z.boolean().default(false),
});

const WorklistRoute = (): React.ReactNode => {
  // The day rail is part of the scheduling feature, so it lives behind the same flag as Appointments.
  // No `isReady` gate: nothing redirects on this, so the rail simply appears if and when the flag
  // resolves true — the readiness wait exists to stop a route guard bouncing someone mid-load.
  const { enabled: scheduleEnabled } = useScheduleEnabled();
  const { timelineNarration } = useFlags();
  const {
    organizationId: urlOrganizationId,
    assignedTo: urlAssignedTo,
    due: urlDue,
    pageIndex,
    task,
    taskPatientId,
    showFlagged,
    // oxlint-disable-next-line no-use-before-define
  } = Route.useSearch();
  // oxlint-disable-next-line no-use-before-define
  const navigate = useNavigate({ from: Route.fullPath });
  const [storedOrganizationId, setStoredOrganizationId] = useLocalStorage<string | undefined>(
    PATIENT_LIST_ORG_STORAGE_KEY,
    undefined,
  );
  const [storedAssignedTo, setStoredAssignedTo] = useLocalStorage<string | undefined>(
    WORKLIST_ASSIGNED_TO_STORAGE_KEY,
    undefined,
  );
  const [storedDue, setStoredDue] = useLocalStorage<WorklistDueFilter | undefined>(WORKLIST_DUE_STORAGE_KEY, undefined);

  // URL param wins (bookmarks/shared links); fall back to the sticky stored value.
  const organizationId = urlOrganizationId ?? storedOrganizationId;
  const assignedTo = urlAssignedTo ?? storedAssignedTo;
  // The URL value is schema-validated but the stored one isn't, so a tampered value is dropped here.
  // First-run default is 'due' (issue #8928) — it only reaches users with no stored pick, since the
  // pill is sticky.
  const due = (urlDue === 'overdue' ? 'due' : urlDue) ?? parseStoredWorklistDue(storedDue) ?? 'due';

  return (
    <WorklistPage
      showDaySchedule={scheduleEnabled}
      morningBriefingSlot={
        timelineNarration === true ? (
          <Suspense fallback={null}>
            <MorningBriefingButton />
          </Suspense>
        ) : null
      }
      organizationId={organizationId}
      // Changing the org also clears the assignee: the pill's options are scoped to the org's
      // directory, so a practitioner picked under the old org may not exist in the new one — the
      // pill would silently read "Anyone" while the list stayed narrowed by the stale id.
      setOrganizationId={(organizationId) => {
        setStoredOrganizationId(organizationId);
        setStoredAssignedTo(undefined);
        void navigate({ search: (prev) => ({ ...prev, organizationId, assignedTo: undefined, pageIndex: 0 }) });
      }}
      assignedTo={assignedTo}
      setAssignedTo={(assignedTo) => {
        setStoredAssignedTo(assignedTo);
        void navigate({ search: (prev) => ({ ...prev, assignedTo, pageIndex: 0 }) });
      }}
      due={due}
      setDue={(due) => {
        setStoredDue(due);
        void navigate({ search: (prev) => ({ ...prev, due, pageIndex: 0 }) });
      }}
      showFlagged={showFlagged}
      setShowFlagged={(showFlagged) => {
        void navigate({ search: (prev) => ({ ...prev, showFlagged, pageIndex: 0 }) });
      }}
      pageIndex={pageIndex}
      setPageIndex={(pageIndex) => {
        void navigate({ search: (prev) => ({ ...prev, pageIndex }), replace: true });
      }}
      taskId={task}
      taskPatientId={taskPatientId}
      // Replaced rather than pushed: a practitioner clicking down a queue of tasks shouldn't have to
      // walk back through every one of them to leave the worklist.
      openTask={(taskPatientId, task) => {
        void navigate({ search: (prev) => ({ ...prev, task, taskPatientId }), replace: true });
      }}
      // `task` cleared so the panel opens on a blank form rather than whichever task was open.
      openNewTask={(taskPatientId) => {
        void navigate({ search: (prev) => ({ ...prev, task: undefined, taskPatientId }), replace: true });
      }}
      closeTask={() => {
        void navigate({
          search: (prev) => ({ ...prev, task: undefined, taskPatientId: undefined }),
          replace: true,
        });
      }}
    />
  );
};

/**
 * No `crumb` of its own, deliberately: the `_auth` layout above already contributes "Home", and
 * `Breadcrumbs` renders every match that has one — so adding it here reads "Home / Home".
 */
export const Route = createFileRoute('/_auth/home/')({
  component: WorklistRoute,
  validateSearch: WorklistSearchSchema,
});
